import { hasSupabaseConfig, supabase } from '../../../shared/api/supabaseClient.js';
import { loadRaidOverview } from '../../raid-overview/api/raidOverviewApi.js';
import {
  assertRaidPracticePreviewWritable,
  isRaidPracticeMockOnlyPreview,
  raidPracticeActualRecordsForDisplay,
} from '../domain/raidPracticePreview.js';

const ADMIN_ERRORS = {
  authentication_required: '로그인 세션을 확인할 수 없습니다. 다시 로그인해주세요.',
  active_union_membership_required: '활성 유니온 가입 정보가 필요합니다.',
  union_admin_required: '리더 또는 부리더만 전체 멤버 모의전을 관리할 수 있습니다.',
  union_roster_member_not_found: '현재 유니온 명단에서 해당 멤버를 확인할 수 없습니다.',
  raid_season_not_found: '선택한 레이드 차수를 찾을 수 없습니다.',
  admin_practice_record_not_found: '수정할 모의전 기록을 찾을 수 없습니다.',
  practice_slot_already_exists: '해당 멤버의 같은 보스·슬롯에 이미 모의전 기록이 있습니다.',
  invalid_season_no: '레이드 차수를 다시 확인해주세요.',
  invalid_boss_no: '보스를 다시 선택해주세요.',
  invalid_slot_no: '슬롯 번호를 다시 확인해주세요.',
  invalid_practice_damage: '모의전 데미지는 0보다 크게 입력해주세요.',
  invalid_practice_deck: '덱은 최대 5명까지 입력할 수 있습니다.',
  practice_title_too_long: '제목은 100자 이내로 입력해주세요.',
  practice_note_too_long: '메모는 500자 이내로 입력해주세요.',
};

function requireClient() {
  if (!hasSupabaseConfig || !supabase) throw new Error('서비스 연결 설정을 확인할 수 없습니다.');
  return supabase;
}

function numberValue(value, fallback = 0) {
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
}

function isMissingAdminRpc(error, functionName) {
  const raw = String(error?.message || '');
  return new RegExp(`${functionName}|function .* does not exist|schema cache`, 'i').test(raw);
}

function createAdminError(error, fallback = '전체 멤버 모의전을 처리하지 못했습니다.') {
  const raw = String(error?.message || '');
  const code = Object.keys(ADMIN_ERRORS).find((item) => raw.includes(item));
  const next = new Error(ADMIN_ERRORS[code] || raw || fallback);
  next.code = code || error?.code || 'unknown_error';
  return next;
}

function missingSetupError() {
  const error = new Error('전체 멤버 모의전 서버 설정이 아직 적용되지 않았습니다.');
  error.code = 'practice_setup_required';
  return error;
}

function normalizePractice(raw = {}) {
  return {
    id: String(raw.id || ''),
    memberOpenid: String(raw.member_openid || raw.openid || ''),
    memberNickname: String(raw.member_nickname || raw.nickname || ''),
    bossNo: numberValue(raw.boss_no),
    slotNo: numberValue(raw.slot_no, 1),
    title: String(raw.title || ''),
    damageEok: numberValue(raw.damage_eok),
    deck: Array.isArray(raw.deck) ? raw.deck : [],
    used: Boolean(raw.used),
    note: String(raw.note || ''),
    createdAt: raw.created_at || null,
    updatedAt: raw.updated_at || null,
  };
}

function normalizeSeasonRows(rows = []) {
  return (Array.isArray(rows) ? rows : []).map((season) => ({
    seasonNo: numberValue(season.season_no),
    status: String(season.status || 'draft'),
  }));
}

function adminWritePayload(payload = {}) {
  return {
    title: String(payload.title || '').trim() || null,
    damage_eok: Number(payload.damageEok),
    deck: (Array.isArray(payload.deck) ? payload.deck : [])
      .map((value) => String(value ?? '').trim())
      .filter(Boolean)
      .slice(0, 5),
    used: Boolean(payload.used),
    note: String(payload.note || '').trim() || null,
  };
}

export async function loadRaidPracticeAdmin(seasonNo = null) {
  const requested = seasonNo === null || seasonNo === undefined || seasonNo === '' ? null : Number(seasonNo);
  const client = requireClient();
  const overview = await loadRaidOverview(requested);
  const overviewSeason = overview?.selectedSeason || null;
  const mockOnlyPreview = isRaidPracticeMockOnlyPreview();

  const { data, error } = await client.rpc('get_union_raid_practice_admin', {
    requested_season_no: requested,
  });

  if (error) {
    if (isMissingAdminRpc(error, 'get_union_raid_practice_admin')) throw missingSetupError();
    throw createAdminError(error);
  }

  const raw = data || {};
  const selected = raw.selected_season && typeof raw.selected_season === 'object'
    ? raw.selected_season
    : null;
  const practiceRecords = (Array.isArray(selected?.practice_records) ? selected.practice_records : [])
    .map(normalizePractice);
  const actualRecords = Array.isArray(overviewSeason?.records) ? overviewSeason.records : [];

  return {
    unionId: String(raw.union_id || overview?.unionId || ''),
    unionName: String(overview?.unionName || ''),
    viewerRole: String(raw.viewer_role || overview?.viewerRole || ''),
    viewerMemberOpenid: String(raw.viewer_member_openid || ''),
    previewMode: mockOnlyPreview ? 'mock-only' : '',
    seasons: overview?.seasons?.length ? overview.seasons : normalizeSeasonRows(raw.seasons),
    selectedSeason: overviewSeason ? {
      id: String(selected?.id || overviewSeason.id || ''),
      seasonNo: numberValue(selected?.season_no || overviewSeason.seasonNo),
      status: String(selected?.status || overviewSeason.status || 'draft'),
      bosses: Array.isArray(overviewSeason.bosses) ? overviewSeason.bosses : [],
      members: Array.isArray(overviewSeason.members) ? overviewSeason.members : [],
      actualRecords: raidPracticeActualRecordsForDisplay(actualRecords),
      practiceRecords,
    } : null,
  };
}

export async function createRaidPracticeAdmin({ seasonNo, memberOpenid, bossNo, payload }) {
  assertRaidPracticePreviewWritable();
  const client = requireClient();
  const write = adminWritePayload(payload);
  const { data, error } = await client.rpc('create_union_raid_practice_admin', {
    target_season_no: Number(seasonNo),
    target_member_openid: String(memberOpenid || '').trim(),
    target_boss_no: Number(bossNo),
    target_slot_no: Number(payload.slotNo),
    target_title: write.title,
    target_damage_eok: write.damage_eok,
    target_deck: write.deck,
    target_used: write.used,
    target_note: write.note,
  });

  if (error) {
    if (isMissingAdminRpc(error, 'create_union_raid_practice_admin')) throw missingSetupError();
    throw createAdminError(error, '모의전 기록을 추가하지 못했습니다.');
  }

  return normalizePractice(data || {});
}

export async function updateRaidPracticeAdmin({ recordId, payload }) {
  assertRaidPracticePreviewWritable();
  const client = requireClient();
  const write = adminWritePayload(payload);
  const { data, error } = await client.rpc('update_union_raid_practice_admin', {
    target_record_id: String(recordId || ''),
    target_title: write.title,
    target_damage_eok: write.damage_eok,
    target_deck: write.deck,
    target_used: write.used,
    target_note: write.note,
  });

  if (error) {
    if (isMissingAdminRpc(error, 'update_union_raid_practice_admin')) throw missingSetupError();
    throw createAdminError(error, '모의전 기록을 수정하지 못했습니다.');
  }

  return normalizePractice(data || {});
}

export async function deleteRaidPracticeAdmin({ recordId }) {
  assertRaidPracticePreviewWritable();
  const client = requireClient();
  const { data, error } = await client.rpc('delete_union_raid_practice_admin', {
    target_record_id: String(recordId || ''),
  });

  if (error) {
    if (isMissingAdminRpc(error, 'delete_union_raid_practice_admin')) throw missingSetupError();
    throw createAdminError(error, '모의전 기록을 삭제하지 못했습니다.');
  }

  return Boolean(data);
}
